import HttpService from '@/services/http';
import { toQuery } from '@/utils/infrastructure';
import request from '@/utils/infrastructure/communication/request';

/**
 * Fetch AI agents from the API
 * @param {object} params - Query parameters (page, limit, search, etc.)
 * @returns {Promise} API response
 */
export async function fetchAIAgents(params = {}) {
  return HttpService.getInstance().request('/ai/agent/manageable', {
    page: params.page || 1,
    limit: params.limit || 5,
    select: 'id,name,isActive,description,iconUrl,provider,isPublic',
    orderBy: 'name',
    ...(params.search && {
      search: params.search,
      searchSelect: ['name', 'description'],
    }),
    ...(params.readPriority && { readPriority: true }),
    // Include filter parameter for OData filtering
    ...(params.filter && { filter: params.filter }),
    // Include other filter parameters (legacy support)
    ...(params.provider && { provider: params.provider }),
    ...(params.modelId && { modelId: params.modelId }),
    ...(params.memoryId && { memoryId: params.memoryId }),
    // Include other parameters that might be passed through
    ...Object.keys(params).reduce((acc, key) => {
      if (
        ![
          'page',
          'limit',
          'select',
          'orderBy',
          'search',
          'searchSelect',
          'readPriority',
          'filter',
          'provider',
          'modelId',
          'memoryId',
        ].includes(key)
      ) {
        acc[key] = params[key];
      }
      return acc;
    }, {}),
  });
}

/**
 * Fetch RPA agents from the API
 * @param {object} params - Query parameters (page, limit, search, etc.)
 * @returns {Promise} API response
 */
export async function fetchRPAAgents(params = {}) {
  return HttpService.getInstance().request('/rpa/agent/paged', {
    page: params.page || 1,
    limit: params.limit || 5,
    select: 'id,name,isActive,deviceName,ip,agentPlatform,status',
    orderBy: 'name',
    ...(params.search && {
      search: params.search,
      searchSelect: ['name', 'deviceName'],
    }),
    ...(params.readPriority && { readPriority: true }),
    // Include filter parameter for OData filtering
    ...(params.filter && { filter: params.filter }),
    // Include other filter parameters (legacy support)
    ...(params.agentPlatform && { agentPlatform: params.agentPlatform }),
    ...(params.status && { status: params.status }),
    // Include other parameters that might be passed through
    ...Object.keys(params).reduce((acc, key) => {
      if (
        ![
          'page',
          'limit',
          'select',
          'orderBy',
          'search',
          'searchSelect',
          'readPriority',
          'filter',
          'agentPlatform',
          'status',
        ].includes(key)
      ) {
        acc[key] = params[key];
      }
      return acc;
    }, {}),
  });
}

/**
 * Get AI agent details by ID
 * @param {string} id - Agent ID
 * @param {object} params - Query parameters (select, expand, etc.)
 * @returns {Promise} API response
 */
export async function getAIAgentById(id, params) {
  return request(`/ai/agent/${id}`, {
    params: toQuery(params),
  });
}

/**
 * Get RPA agent details by ID
 * @param {string} id - Agent ID
 * @param {object} params - Query parameters (select, expand, etc.)
 * @returns {Promise} API response
 */
export async function getRPAAgentById(id, params) {
  return request(`/rpa/agent/${id}`, {
    params: toQuery(params),
  });
}

/**
 * Create a new AI agent
 * @param {object} data - AI agent data
 * @returns {Promise} API response
 */
export async function createAIAgent(data) {
  return request('/ai/agent', {
    method: 'POST',
    data,
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Update an existing AI agent
 * @param {string} id - Agent ID
 * @param {object} data - AI agent data
 * @returns {Promise} API response
 */
export async function updateAIAgent(data) {
  return request(`/ai/agent`, {
    method: 'PUT',
    data,
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Patch RPA agent name only (PATCH request with only name field)
 * @param {string} id - Agent ID
 * @param {object} data - Only name field: { name: string }
 * @returns {Promise} API response
 */
export async function patchRPAAgentName(id, data) {
  return request(`/rpa/agent/${id}`, {
    method: 'PATCH',
    data,
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Delete an AI agent
 * @param {string} id - Agent ID
 * @returns {Promise} API response
 */
export async function deleteAIAgent(id) {
  return request(`/ai/agent/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Delete an RPA agent
 * @param {string} id - Agent ID
 * @returns {Promise} API response
 */
export async function deleteRPAAgent(id) {
  return request(`/rpa/agent/${id}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Toggle AI agent active status
 * @param {string} id - Agent ID
 * @param {boolean} state - New active state
 * @returns {Promise} API response
 */
export async function toggleAIAgentActive(id, state) {
  return request(`/ai/agent/activate/${id}/${state}`, {
    method: 'POST',
  });
}

/**
 * Toggle RPA agent active status
 * @param {string} id - Agent ID
 * @param {boolean} state - New active state
 * @returns {Promise} API response
 */
export async function toggleRPAAgentActive(id, state) {
  return request(`/rpa/agent/activate/${id}/${state}`, {
    method: 'POST',
  });
}

/**
 * Publish AI agent
 * @param {string} id - Agent ID
 * @returns {Promise} API response
 */
export async function publishAIAgent(id) {
  return request(`/ai/agent/publish/${id}`, {
    method: 'POST',
  });
}
